import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  OutreachCategory,
  PlaylistingChannel,
  ReactionChannel,
  SocialLink,
  genreOptions,
} from "../../types/outreach";
import {
  formatLegacySocialHandles,
  resolveSocialLinks,
} from "../../utils/socialLinks";
import { SocialLinksInput } from "./SocialLinksInput";

interface ChannelEditModalProps {
  open: boolean;
  category: OutreachCategory;
  channels: (ReactionChannel | PlaylistingChannel)[];
  onClose: () => void;
  onSave: (updates: Record<string, any>) => void;
  saving?: boolean;
  error?: string | null;
}

interface ChannelFormState {
  channelName: string;
  channelUrl: string;
  subscriberCount: string;
  email: string;
  socialHandles: string;
  socialLinks: SocialLink[];
  likes: string;
  viewCount: string;
  videoCount: string;
  genres: string[];
}

const sanitizeNumberInput = (value: string): number | null | string => {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  const normalized = trimmed.replace(/,/g, "");
  const parsed = Number(normalized);
  if (Number.isNaN(parsed)) {
    return trimmed;
  }

  return parsed;
};

export const ChannelEditModal: React.FC<ChannelEditModalProps> = ({
  open,
  category,
  channels,
  onClose,
  onSave,
  saving = false,
  error = null,
}) => {
  const isMultiple = channels.length > 1;
  const showSocialHandles = category === OutreachCategory.REACTION_CHANNELS;

  const baseState = useMemo<ChannelFormState>(() => {
    const firstChannel = channels[0];
    const initialGenres = firstChannel?.genre
      ? firstChannel.genre
          .split("/")
          .map((genre) => genre.trim())
          .filter(Boolean)
      : [];

    if (channels.length === 1 && firstChannel) {
      const initialSocialLinks = resolveSocialLinks(
        "socialLinks" in firstChannel
          ? (firstChannel as ReactionChannel).socialLinks ?? []
          : undefined,
        "socialHandles" in firstChannel
          ? (firstChannel as ReactionChannel).socialHandles ?? ""
          : ""
      );
      const initialSocialHandles =
        "socialHandles" in firstChannel
          ? (firstChannel as ReactionChannel).socialHandles ?? ""
          : formatLegacySocialHandles(initialSocialLinks);

      return {
        channelName: firstChannel.channelName ?? "",
        channelUrl: firstChannel.channelUrl ?? "",
        subscriberCount: firstChannel.subscriberCount ?? "",
        email: firstChannel.email ?? "",
        socialHandles: initialSocialHandles,
        socialLinks: initialSocialLinks,
        likes:
          firstChannel.likes !== undefined && firstChannel.likes !== null
            ? String(firstChannel.likes)
            : "",
        viewCount:
          firstChannel.viewCount !== undefined &&
          firstChannel.viewCount !== null
            ? String(firstChannel.viewCount)
            : "",
        videoCount:
          firstChannel.videoCount !== undefined &&
          firstChannel.videoCount !== null
            ? String(firstChannel.videoCount)
            : "",
        genres: initialGenres,
      };
    }

    return {
      channelName: "",
      channelUrl: "",
      subscriberCount: "",
      email: "",
      socialHandles: "",
      socialLinks: [],
      likes: "",
      viewCount: "",
      videoCount: "",
      genres: initialGenres,
    };
  }, [channels]);

  const [formState, setFormState] = useState<ChannelFormState>(baseState);

  useEffect(() => {
    if (open) {
      setFormState(baseState);
    }
  }, [baseState, open]);

  const handleFieldChange = (field: keyof ChannelFormState, value: string) => {
    setFormState((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSocialLinksChange = (links: SocialLink[]) => {
    setFormState((prev) => ({
      ...prev,
      socialLinks: links,
      socialHandles: formatLegacySocialHandles(links),
    }));
  };

  const handleGenresChange = (genres: string[]) => {
    setFormState((prev) => ({
      ...prev,
      genres,
    }));
  };

  const handleSubmit = () => {
    const updates: Record<string, any> = {
      genre: formState.genres.join("/"),
    };

    if (!isMultiple) {
      updates.channelName = formState.channelName.trim();
      updates.channelUrl = formState.channelUrl.trim();
      updates.subscriberCount = formState.subscriberCount.trim();
      updates.email = formState.email.trim();
      updates.likes = formState.likes.trim();
      const parsedViewCount = sanitizeNumberInput(formState.viewCount);
      const parsedVideoCount = sanitizeNumberInput(formState.videoCount);

      updates.viewCount = parsedViewCount;
      updates.videoCount = parsedVideoCount;

      if (showSocialHandles) {
        updates.socialLinks = formState.socialLinks;
        updates.socialHandles = formatLegacySocialHandles(formState.socialLinks);
      }
    }

    onSave(updates);
  };

  const canSave = useMemo(() => {
    if (formState.genres.length === 0) {
      return false;
    }

    if (isMultiple) {
      return true;
    }

    return (
      formState.channelName.trim().length > 0 &&
      formState.channelUrl.trim().length > 0
    );
  }, [formState, isMultiple]);

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isMultiple
          ? `Edit ${channels.length} Channels`
          : `Edit ${channels[0]?.channelName ?? "Channel"}`}
      </DialogTitle>
      <DialogContent dividers>
        {isMultiple ? (
          <Box>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Update the genre for the selected channels.
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
              {channels.map((channel) => (
                <Chip
                  key={channel.id}
                  label={channel.channelName}
                  size="small"
                  sx={{ mb: 1 }}
                />
              ))}
            </Stack>
          </Box>
        ) : (
          <Stack spacing={2} sx={{ mb: 2 }}>
            <TextField
              label="Channel Name"
              value={formState.channelName}
              onChange={(event) =>
                handleFieldChange("channelName", event.target.value)
              }
              required
              fullWidth
            />
            <TextField
              label="Channel URL"
              value={formState.channelUrl}
              onChange={(event) =>
                handleFieldChange("channelUrl", event.target.value)
              }
              required
              fullWidth
            />
            <TextField
              label="Subscriber Count"
              value={formState.subscriberCount}
              onChange={(event) =>
                handleFieldChange("subscriberCount", event.target.value)
              }
              fullWidth
            />
            <TextField
              label="Email"
              value={formState.email}
              onChange={(event) => handleFieldChange("email", event.target.value)}
              fullWidth
            />
            {showSocialHandles && (
              <SocialLinksInput
                value={formState.socialLinks}
                legacyValue={formState.socialHandles}
                onChange={handleSocialLinksChange}
                helperText="Paste @handles or full URLs"
              />
            )}
            <TextField
              label="Likes"
              value={formState.likes}
              onChange={(event) => handleFieldChange("likes", event.target.value)}
              fullWidth
            />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Total Views"
                value={formState.viewCount}
                onChange={(event) =>
                  handleFieldChange("viewCount", event.target.value)
                }
                fullWidth
              />
              <TextField
                label="Videos"
                value={formState.videoCount}
                onChange={(event) =>
                  handleFieldChange("videoCount", event.target.value)
                }
                fullWidth
              />
            </Stack>
          </Stack>
        )}

        <Autocomplete
          multiple
          options={genreOptions}
          value={formState.genres}
          onChange={(event, newValue) => handleGenresChange(newValue)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Genre"
              required
              placeholder="Select genre"
            />
          )}
        />

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!canSave || saving}
          variant="contained"
        >
          {saving ? "Saving..." : "Save Changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChannelEditModal;
